import { MembershipStatusEnum } from '@sr-sdks/api-sdk-axios';
import { Dialog } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import { isWorkspaceReadyForMembership } from '../forms/workspaceDetails/shared/membershipReadiness';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import {
  userIsWorkspaceAdmin,
  userIsWorkspaceOwner,
} from '../utils/helpers/common';
import { RequestMembershipModal } from './RequestMembershipModal';

/**
 * Site-wide membership prompt for a workspace sitting at ApplicationSent: the
 * forced agreement, or, while the saved details are incomplete, a closable
 * prompt pointing at them.
 *
 * That state has no other route forward: the owner must accept the agreement to move
 * it to InReview (WorkspaceController "Case 3" — applicationSent -> inReview with an
 * existing membershipId). MembershipActionsMenu deliberately offers nothing there —
 * `showApplyMembership` is false because ApplicationSent counts as in-progress, and
 * `showSubmitReview` only covers AwaitingUpdate — so without this modal the workspace
 * is stuck with no way to proceed.
 *
 * Lives in its own component so both the MSR2 (WorkspaceLayout) and MSR3
 * (MSR3BaseLayout) shells mount identical behaviour, rather than the prompt appearing
 * only on ?v=2.
 *
 * Self-guarding: renders nothing outside a workspace route, so it is safe to mount in
 * a shared layout that also serves non-workspace pages.
 */
export const MembershipApplicationGate = () => {
  const { workspaceId } = useParams();
  const { currentUser } = useCurrentUserAuth();
  const { data: workspace } = useQuery(queryOptionsGetWorkspace(workspaceId));

  const workspaceDetailsPath = `/workspace/${workspaceId}/manage-workspace/details`;
  const isOnWorkspaceDetails = Boolean(useMatch(workspaceDetailsPath));

  // The query keeps the previous workspace's data while the next one loads.
  if (!workspaceId || !currentUser || workspace?._id !== workspaceId) {
    return null;
  }

  const isOwnerOrAdmin =
    userIsWorkspaceOwner(currentUser, workspaceId) ||
    userIsWorkspaceAdmin(currentUser, workspaceId);

  if (!isOwnerOrAdmin) return null;

  if (workspace.membershipStatus !== MembershipStatusEnum.ApplicationSent) {
    return null;
  }

  // Staff may send before the details exist (APP-2386); forcing the agreement
  // then would block every page, including the one that fixes them.
  if (!isWorkspaceReadyForMembership(workspace)) {
    return isOnWorkspaceDetails ? null : (
      <IncompleteDetailsPrompt
        key={workspaceId}
        detailsPath={workspaceDetailsPath}
      />
    );
  }

  // No onSuccess handler needed: visibility is derived from the workspace status, so
  // the modal disappears once the mutation invalidates the workspace query.
  return <RequestMembershipModal isForced workspace={workspace} />;
};

const IncompleteDetailsPrompt = ({ detailsPath }: { detailsPath: string }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Dialog
      heading="Workspace Details Incomplete"
      actionButtonProps={{
        children: 'View Workspace Details',
        onClick: () => {
          setIsOpen(false);
          navigate(detailsPath);
        },
      }}
      cancelButtonProps={{
        children: 'Close',
        onClick: () => setIsOpen(false),
        variant: 'outline',
      }}
      description="Please update your workspace details to apply for a membership"
      hasCloseButton={false}
      open={isOpen}
      onOpenChange={setIsOpen}
    />
  );
};
